import {Mask} from './Mask';
import {Gestures} from '../Library/Gestures';
import {transitionByClass} from '../Library/Animation';
import {isMobile} from '../Library/Viewport';

const dismissSwipeDistance = 65

const defaults = {
    onClose: () => {},
    onCloseBlocked: () => {},
    mobileOnlyMask: false,
    extraCloseHandlers: [],
    closeOutside: true,
    animateMask: true,
    closeEvent: 'closesidepanel',
    beforeClose: () => true,
};

/**
 * @param {HTMLElement} drawer
 * @param {{[onClose]: function, [mobileOnlyMask]: boolean, [extraCloseHandlers]: array, [closeOutside]: boolean, [animateMask]: boolean}} options
 */
export const Drawer = (drawer, options) => {
    options = {...defaults, ...options};

    const isClickableHandle = (target) => {
        if (target.parentNode !== drawer) {
            return false;
        }

        return target.classList.contains('handle') || target.nodeName === 'HEADER';
    };

    const onClick = (event) => {
        // The expand/dismiss tap is a mobile bottom-sheet affordance; on
        // desktop it would dismiss the panel from plain header whitespace.
        if (!isMobile() || !isClickableHandle(event.target)) {
            return;
        }

        if (drawer.classList.contains('full-height')) {
            mask.hide();

            return;
        }

        drawer.classList.add('full-height');

        transitionByClass(drawer, 'animate');
    };

    const closeDrawer = () => {
        drawer.classList.remove('active', 'full-height');

        transitionByClass(drawer, 'animate');

        drawer.removeEventListener('click', onClick);
        document.removeEventListener(options.closeEvent, mask.hide);

        if (options.onClose) {
            options.onClose();
        }
    };

    const mask = Mask({
        excludedCloseHandlers: [options.maskExcludedSelector ?? drawer, '.toast-container'],
        onClose: closeDrawer,
        mobileOnly: options.mobileOnlyMask,
        closeOutside: options.closeOutside,
        animate: options.animateMask,
        beforeClose: options.beforeClose,
        onCloseBlocked: options.onCloseBlocked,
    });

    const open = () => {
        const moveDrawer = (event) => {
            if (event.movement.vertical.direction === 'up' && drawer.classList.contains('full-height')) {
                return;
            }

            if (event.movement.vertical.direction === 'up') {
                drawer.style.height = `calc(50dvh + ${event.movement.vertical.delta}px)`;

                if (drawer.getBoundingClientRect().top < 65) {
                    drawer.style.height = `calc(100dvh - 65px)`;
                }
            }

            if (drawer.classList.contains('full-height')) {
                drawer.style.height = `calc((100dvh - (65px  + ${event.movement.vertical.delta}px))`;
            } else {
                if (drawer.getBoundingClientRect().top < 65) {
                    drawer.style.height = `calc(100dvh - 65px)`;
                } else {
                    drawer.style.height = `calc(50dvh - ${event.delta.y}px)`;
                }
            }
        };

        const stopMovement = async (event) => {
            if (event.movement.vertical.direction === 'down' && event.movement.vertical.delta > dismissSwipeDistance) {
                drawer.style.height = '';

                if (!options.beforeClose()) {
                    options.onCloseBlocked();

                    return;
                }

                drawer.classList.remove('active', 'full-height');

                transitionByClass(drawer, 'animate-swipe');

                drawer.removeEventListener('click', onClick);

                await mask.hide();
            }

            if (event.movement.vertical.direction === 'up' && event.movement.vertical.delta > dismissSwipeDistance && !drawer.classList.contains('full-height')) {
                drawer.style.height = 'calc(100dvh - 65px)';

                await transitionByClass(drawer, 'animate-swipe');

                drawer.classList.add('full-height');
            }

            drawer.style.height = '';
        };

        Gestures({
            handles: [drawer.querySelector('.handle'), drawer.querySelector(':scope > header')],
            onMove: moveDrawer,
            onSwipe: stopMovement,
        });

        mask.render();

        drawer.querySelector('.content').scrollTop = 0;

        drawer.classList.add('active');

        drawer.addEventListener('click', onClick);
        document.addEventListener(options.closeEvent, mask.hide);

        transitionByClass(drawer, 'animate');
    };

    return {
        open: open,
        close: mask.hide,
    };
};
